import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { listContractors, createContractor } from 'services/contractorService'
import { createContract, updateContract } from 'services/contractService'
import { fetchUserBps } from 'services/BpService'
import SearchableSelect from 'components/SearchableSelect'
import { severityColour } from 'features/dashboard/charts/scoreColour'

const TYPES = [
  ['security', 'Security'], ['cleaning', 'Cleaning'], ['m_and_e', 'M&E'],
  ['life_safety', 'Life Safety'], ['grounds_maintenance', 'Grounds Maintenance'],
  ['pest_control', 'Pest Control'], ['window_cleaning', 'Window Cleaning'],
]
const CRITS = ['low', 'medium', 'high', 'critical']
const BASES = [['annual', 'Annual'], ['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['one_off', 'One-off']]
const UNITS = [['week', 'Weekly'], ['month', 'Monthly'], ['year', 'Yearly']]

const cadencePhrase = s => {
  const n = Math.max(1, parseInt(s.recurrenceInterval, 10) || 1)
  const unit = { week: 'week', month: 'month', year: 'year' }[s.recurrenceUnit] || 'month'
  return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`
}

const emptyKpi = () => ({ name: '', target: '', unit: '', frequency: '' })
const emptySla = () => ({ metric: '', target: '', unit: '', breachConsequence: '', monitorAsTask: false, recurrenceInterval: 1, recurrenceUnit: 'month', requiresSignin: false })

const ContractModal = ({ contract, onClose, onSaved }) => {
  const isEdit = !!contract
  const canViewValue = !contract || !contract.valueRestricted

  const [contractors, setContractors] = useState([])
  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(false)
  const [bpSearch, setBpSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [creatingContractor, setCreatingContractor] = useState(false)
  const [newContractor, setNewContractor] = useState({ name: '', email: '', company: '', phone: '' })
  const [creatingBusy, setCreatingBusy] = useState(false)

  const [form, setForm] = useState(() => ({
    contractorId: contract?.contractor?.id || '',
    contractType: contract?.contractType || 'cleaning',
    criticality: contract?.criticality || 'medium',
    reference: contract?.reference || '',
    supplierReference: contract?.supplierReference || '',
    title: contract?.title || '',
    status: contract?.status || 'active',
    value: contract?.value ?? '',
    valueBasis: contract?.valueBasis || 'annual',
    currency: contract?.currency || 'GBP',
    totalContractValue: contract?.totalContractValue ?? '',
    paymentTerms: contract?.paymentTerms || '',
    billingFrequency: contract?.billingFrequency || '',
    startDate: contract?.startDate || '',
    endDate: contract?.endDate || '',
    signedDate: contract?.signedDate || '',
    renewalType: contract?.renewal?.type || '',
    autoRenews: contract?.renewal?.autoRenews || false,
    noticePeriodDays: contract?.renewal?.noticePeriodDays ?? '',
    nextReviewDate: contract?.renewal?.nextReviewDate || '',
    renewalNotes: contract?.renewal?.notes || '',
    accountManagerName: contract?.accountManager?.name || '',
    accountManagerEmail: contract?.accountManager?.email || '',
    accountManagerPhone: contract?.accountManager?.phone || '',
    scopeOfServices: contract?.scopeOfServices || '',
    notes: contract?.notes || '',
    buildingIds: (contract?.buildings || []).map(b => b.id),
    kpis: (contract?.kpis || []).map(k => ({ name: k.name, target: k.target, unit: k.unit || '', frequency: k.frequency || '' })),
    slas: (contract?.slas || []).map(s => ({
      id: s.id, metric: s.metric, target: s.target, unit: s.unit || '', breachConsequence: s.breachConsequence || '',
      monitorAsTask: !!s.monitorAsTask, recurrenceInterval: s.recurrenceInterval || 1, recurrenceUnit: s.recurrenceUnit || 'month', requiresSignin: !!s.requiresSignin,
    })),
  }))

  useEffect(() => {
    listContractors().then(res => setContractors(res?.data?.data?.contractors || [])).catch(() => {})
    setBpsLoading(true)
    fetchUserBps({ page: 1, sort: 'name', filters: '&limit=250' })
      .then(res => setBps(res?.data?.entries || res?.data?.data || []))
      .catch(() => {})
      .finally(() => setBpsLoading(false))
  }, []) // eslint-disable-line

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }))
  const toggleBuilding = id => setForm(f => ({
    ...f, buildingIds: f.buildingIds.includes(id) ? f.buildingIds.filter(x => x !== id) : [...f.buildingIds, id],
  }))

  const filteredBps = useMemo(() => {
    const q = bpSearch.trim().toLowerCase()
    if (!q) return bps || []
    return (bps || []).filter(b =>
      [b.name, b.uprn, b.street, b.city, b.postalCode]
        .some(v => (v || '').toString().toLowerCase().includes(q)))
  }, [bps, bpSearch])

  const setRow = (coll, i, key, val) => setForm(f => ({
    ...f, [coll]: f[coll].map((r, idx) => (idx === i ? { ...r, [key]: val } : r)),
  }))
  const addRow = (coll, factory) => setForm(f => ({ ...f, [coll]: [...f[coll], factory()] }))
  const removeRow = (coll, i) => setForm(f => ({ ...f, [coll]: f[coll].filter((_, idx) => idx !== i) }))

  const createContractorInline = async () => {
    const name = newContractor.name.trim()
    const email = newContractor.email.trim()
    if (!name || !email) { toast.error('Enter a name and email for the new contractor.'); return }
    setCreatingBusy(true)
    try {
      const payload = { name, email }
      if (newContractor.company.trim()) payload.company = newContractor.company.trim()
      if (newContractor.phone.trim()) payload.phone = newContractor.phone.trim()
      const before = new Set(contractors.map(c => c.id))
      await createContractor(payload)
      const listRes = await listContractors()
      const list = listRes?.data?.data?.contractors || []
      setContractors(list)
      const created = list.find(c => !before.has(c.id))
        || list.find(c => (c.email || '').toLowerCase() === email.toLowerCase())
      if (created?.id) set('contractorId', created.id)
      setCreatingContractor(false)
      setNewContractor({ name: '', email: '', company: '', phone: '' })
      toast.success('Contractor created and selected.')
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not create the contractor.')
    } finally {
      setCreatingBusy(false)
    }
  }

  const save = async () => {
    if (!form.contractorId) { toast.error('Choose a contractor.'); return }
    setSaving(true)
    try {
      const payload = { ...form }
      if (!canViewValue) {
        delete payload.value; delete payload.valueBasis; delete payload.currency
        delete payload.totalContractValue; delete payload.paymentTerms; delete payload.billingFrequency
      }
      if (isEdit) await updateContract(contract.id, payload)
      else await createContract(payload)
      toast.success(isEdit ? 'Contract updated.' : 'Contract created.')
      onSaved && onSaved()
      onClose()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save the contract.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onMouseDown={onClose}>
      <Panel onMouseDown={e => e.stopPropagation()}>
        <Head>
          <h3>{isEdit ? 'Edit contract' : 'New contract'}</h3>
          <Close type='button' onClick={onClose}>✕</Close>
        </Head>

        <Body>
          <Section>Contractor</Section>
          <ContractorRow>
            <Field style={{ flex: 1 }}><Label>Contractor *</Label>
              <SearchableSelect
                value={form.contractorId}
                onChange={v => set('contractorId', v)}
                options={contractors.map(c => ({ value: c.id, label: c.name, sublabel: c.company || undefined }))}
                placeholder='Select…'
                searchPlaceholder='Search contractors…'
                ariaLabel='Contractor'
              />
            </Field>
            <AddBtn type='button' onClick={() => setCreatingContractor(v => !v)}>
              {creatingContractor ? 'Cancel' : '+ New contractor'}
            </AddBtn>
          </ContractorRow>

          {creatingContractor && (
            <NewContractorPanel>
              <Grid>
                <Field><Label>Name *</Label><Input value={newContractor.name} onChange={e => setNewContractor(n => ({ ...n, name: e.target.value }))} placeholder='Contractor name' /></Field>
                <Field><Label>Email *</Label><Input type='email' value={newContractor.email} onChange={e => setNewContractor(n => ({ ...n, email: e.target.value }))} placeholder='name@company.com' /></Field>
                <Field><Label>Company</Label><Input value={newContractor.company} onChange={e => setNewContractor(n => ({ ...n, company: e.target.value }))} /></Field>
                <Field><Label>Phone</Label><Input value={newContractor.phone} onChange={e => setNewContractor(n => ({ ...n, phone: e.target.value }))} /></Field>
              </Grid>
              <NewContractorActions>
                <Primary type='button' disabled={creatingBusy} onClick={createContractorInline}>
                  {creatingBusy ? 'Creating…' : 'Create contractor'}
                </Primary>
              </NewContractorActions>
            </NewContractorPanel>
          )}

          {!form.contractorId && !creatingContractor && (
            <GateHint>Select or create a contractor to fill in the rest of the contract.</GateHint>
          )}

          <GatedFieldset disabled={!form.contractorId}>
          <Section>Basics</Section>
          <Grid>
            <Field><Label>Type</Label>
              <SearchableSelect
                value={form.contractType}
                onChange={v => set('contractType', v)}
                options={TYPES.map(([k, l]) => ({ value: k, label: l }))}
                ariaLabel='Contract type'
              />
            </Field>
            <Field><Label>Criticality</Label>
              <SearchableSelect
                value={form.criticality}
                onChange={v => set('criticality', v)}
                options={CRITS.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1), color: severityColour(c) }))}
                ariaLabel='Criticality'
              />
            </Field>
            <Field><Label>Reference</Label><Input value={form.reference} onChange={e => set('reference', e.target.value)} /></Field>
            <Field><Label>Supplier reference</Label><Input value={form.supplierReference} onChange={e => set('supplierReference', e.target.value)} /></Field>
            <Field $wide><Label>Title</Label><Input value={form.title} onChange={e => set('title', e.target.value)} /></Field>
          </Grid>

          <Section>Buildings covered</Section>
          <Input placeholder='Search buildings…' value={bpSearch} onChange={e => setBpSearch(e.target.value)} />
          <BpList>
            {bpsLoading && filteredBps.length === 0 && <Muted>Loading buildings…</Muted>}
            {!bpsLoading && filteredBps.length === 0 && <Muted>No buildings found.</Muted>}
            {filteredBps.map(b => {
              const address = [b.street, b.city, b.postalCode].filter(Boolean).join(', ')
              return (
                <BpItem key={b.id}>
                  <input type='checkbox' checked={form.buildingIds.includes(b.id)} onChange={() => toggleBuilding(b.id)} />
                  <BpText>
                    <BpName>{b.name}</BpName>
                    <BpMeta>
                      {b.uprn ? `UPRN ${b.uprn}` : 'No UPRN'}{address ? ` · ${address}` : ''}
                    </BpMeta>
                  </BpText>
                </BpItem>
              )
            })}
          </BpList>

          {canViewValue && (
            <>
              <Section>Commercials</Section>
              <Grid>
                <Field><Label>Value</Label><Input type='number' value={form.value} onChange={e => set('value', e.target.value)} /></Field>
                <Field><Label>Basis</Label>
                  <SearchableSelect
                    value={form.valueBasis}
                    onChange={v => set('valueBasis', v)}
                    options={BASES.map(([k, l]) => ({ value: k, label: l }))}
                    ariaLabel='Value basis'
                  />
                </Field>
                <Field><Label>Total contract value</Label><Input type='number' value={form.totalContractValue} onChange={e => set('totalContractValue', e.target.value)} /></Field>
                <Field><Label>Payment terms</Label><Input value={form.paymentTerms} onChange={e => set('paymentTerms', e.target.value)} /></Field>
                <Field><Label>Billing frequency</Label><Input value={form.billingFrequency} onChange={e => set('billingFrequency', e.target.value)} /></Field>
              </Grid>
            </>
          )}

          <Section>Dates &amp; renewal</Section>
          <Grid>
            <Field><Label>Start</Label><Input type='date' value={form.startDate} onChange={e => set('startDate', e.target.value)} /></Field>
            <Field><Label>End (expiry)</Label><Input type='date' value={form.endDate} onChange={e => set('endDate', e.target.value)} /></Field>
            <Field><Label>Signed</Label><Input type='date' value={form.signedDate} onChange={e => set('signedDate', e.target.value)} /></Field>
            <Field><Label>Renewal</Label>
              <SearchableSelect
                value={form.renewalType}
                onChange={v => set('renewalType', v || '')}
                options={[
                  { value: 'auto_renew', label: 'Auto-renew' },
                  { value: 'manual', label: 'Manual' },
                  { value: 'fixed_term', label: 'Fixed term' },
                ]}
                placeholder='—'
                clearable
                clearLabel='—'
                ariaLabel='Renewal'
              />
            </Field>
            <Field><Label>Notice period (days)</Label><Input type='number' value={form.noticePeriodDays} onChange={e => set('noticePeriodDays', e.target.value)} /></Field>
            <Field><Label>Next review</Label><Input type='date' value={form.nextReviewDate} onChange={e => set('nextReviewDate', e.target.value)} /></Field>
            <CheckField><input type='checkbox' checked={form.autoRenews} onChange={e => set('autoRenews', e.target.checked)} /> Auto-renews</CheckField>
          </Grid>

          <Section>Account manager</Section>
          <Grid>
            <Field><Label>Name</Label><Input value={form.accountManagerName} onChange={e => set('accountManagerName', e.target.value)} /></Field>
            <Field><Label>Email</Label><Input type='email' value={form.accountManagerEmail} onChange={e => set('accountManagerEmail', e.target.value)} /></Field>
            <Field><Label>Phone</Label><Input value={form.accountManagerPhone} onChange={e => set('accountManagerPhone', e.target.value)} /></Field>
          </Grid>

          <Section>Scope &amp; notes</Section>
          <Textarea value={form.scopeOfServices} onChange={e => set('scopeOfServices', e.target.value)} placeholder='Scope of services…' />
          <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder='Notes…' />

          <RepeaterHead><Section>KPIs</Section><AddBtn type='button' onClick={() => addRow('kpis', emptyKpi)}>+ Add KPI</AddBtn></RepeaterHead>
          {form.kpis.map((k, i) => (
            <RepRow key={i}>
              <Input placeholder='Name' value={k.name} onChange={e => setRow('kpis', i, 'name', e.target.value)} />
              <Input placeholder='Target' value={k.target} onChange={e => setRow('kpis', i, 'target', e.target.value)} />
              <Input placeholder='Unit' value={k.unit} onChange={e => setRow('kpis', i, 'unit', e.target.value)} />
              <Input placeholder='Frequency' value={k.frequency} onChange={e => setRow('kpis', i, 'frequency', e.target.value)} />
              <RemBtn type='button' onClick={() => removeRow('kpis', i)}>✕</RemBtn>
            </RepRow>
          ))}

          <RepeaterHead><Section>SLAs</Section><AddBtn type='button' onClick={() => addRow('slas', emptySla)}>+ Add SLA</AddBtn></RepeaterHead>
          {form.slas.map((s, i) => (
            <SlaBlock key={i}>
              <RepRow>
                <Input placeholder='Metric' value={s.metric} onChange={e => setRow('slas', i, 'metric', e.target.value)} />
                <Input placeholder='Target' value={s.target} onChange={e => setRow('slas', i, 'target', e.target.value)} />
                <Input placeholder='Unit' value={s.unit} onChange={e => setRow('slas', i, 'unit', e.target.value)} />
                <RemBtn type='button' onClick={() => removeRow('slas', i)}>✕</RemBtn>
              </RepRow>
              <Input placeholder='Breach consequence' value={s.breachConsequence} onChange={e => setRow('slas', i, 'breachConsequence', e.target.value)} />
              <MonitorRow>
                <CheckField><input type='checkbox' checked={s.monitorAsTask} onChange={e => setRow('slas', i, 'monitorAsTask', e.target.checked)} /> Monitor as a recurring task</CheckField>
                {s.monitorAsTask && (
                  <>
                    <SmallLabel>Every</SmallLabel>
                    <NumInput type='number' min='1' value={s.recurrenceInterval} onChange={e => setRow('slas', i, 'recurrenceInterval', e.target.value)} />
                    <div style={{ width: 130 }}>
                      <SearchableSelect
                        value={s.recurrenceUnit}
                        onChange={v => setRow('slas', i, 'recurrenceUnit', v)}
                        options={UNITS.map(([k, l]) => ({ value: k, label: l }))}
                        ariaLabel='Recurrence unit'
                      />
                    </div>
                    <CheckField><input type='checkbox' checked={s.requiresSignin} onChange={e => setRow('slas', i, 'requiresSignin', e.target.checked)} /> Sign-in proof</CheckField>
                  </>
                )}
              </MonitorRow>
              {s.monitorAsTask && (
                <MonitorHelp>
                  On save, a recurring task is created for the contractor — one per selected building
                  {form.buildingIds.length ? ` (${form.buildingIds.length})` : ''} — due {cadencePhrase(s)}, up to the contract end date.
                  Each occurrence is the check that this SLA was met; completing it{s.requiresSignin ? ' with a contractor sign-in' : ''} is the evidence,
                  and the SLA's status (on track / at risk / breached) is measured from those completions rather than entered by hand. Untick to stop.
                </MonitorHelp>
              )}
            </SlaBlock>
          ))}
          </GatedFieldset>
        </Body>

        <Footer>
          <Ghost type='button' onClick={onClose}>Cancel</Ghost>
          <Primary type='button' disabled={saving || !form.contractorId} onClick={save}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create contract')}</Primary>
        </Footer>
      </Panel>
    </Overlay>
  )
}

export default ContractModal

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(12,18,41,0.45); z-index: 1300; display: flex; align-items: flex-start; justify-content: center; padding: 32px 16px; overflow-y: auto;`
const Panel = styled.div`width: 760px; max-width: 100%; background: #fff; border-radius: 14px; box-shadow: 0 24px 60px rgba(12,18,41,0.3); display: flex; flex-direction: column; max-height: calc(100vh - 64px);`
const Head = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eee; h3 { margin: 0; font-size: 16px; color: #0C1229; }`
const Close = styled.button`border: none; background: none; font-size: 16px; cursor: pointer; color: #8A8F9C;`
const Body = styled.div`padding: 18px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px;`
const Section = styled.div`font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #8A8F9C; margin-top: 8px;`
const Grid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; @media (max-width: 640px) { grid-template-columns: 1fr; }`
const Field = styled.label`display: flex; flex-direction: column; gap: 4px; ${({ $wide }) => $wide && 'grid-column: 1 / -1;'}`
const Label = styled.span`font-size: 12px; font-weight: 600; color: #4A4F5E;`
const SmallLabel = styled.span`font-size: 12px; color: #6b7280;`
const Input = styled.input`font: inherit; padding: 8px 10px; border: 1px solid rgba(12,18,41,0.16); border-radius: 8px; font-size: 13px; outline: none; &:focus { border-color: #2aa6b3; }`
const NumInput = styled(Input)`max-width: 80px;`
const Textarea = styled.textarea`font: inherit; padding: 8px 10px; border: 1px solid rgba(12,18,41,0.16); border-radius: 8px; font-size: 13px; min-height: 56px; resize: vertical;`
const BpList = styled.div`display: flex; flex-direction: column; gap: 4px; max-height: 160px; overflow-y: auto; flex-shrink: 0; border: 1px solid rgba(12,18,41,0.08); border-radius: 8px; padding: 8px;`
const BpItem = styled.label`display: flex; align-items: flex-start; gap: 8px; padding: 4px 2px; font-size: 13px; color: #111827; cursor: pointer; input { margin-top: 3px; }`
const BpText = styled.span`display: flex; flex-direction: column; gap: 1px; min-width: 0;`
const BpName = styled.span`font-weight: 600; color: #110c2f;`
const BpMeta = styled.span`font-size: 11.5px; color: #8A8F9C;`
const Muted = styled.div`font-size: 12px; color: #8A8F9C; padding: 4px;`
const CheckField = styled.label`display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #4A4F5E;`
const RepeaterHead = styled.div`display: flex; align-items: center; justify-content: space-between;`
const AddBtn = styled.button`font: inherit; border: 1px solid rgba(12,18,41,0.16); background: #fff; border-radius: 999px; padding: 5px 12px; font-size: 12px; font-weight: 700; cursor: pointer; &:hover { border-color: #2aa6b3; color: #2aa6b3; }`
const RepRow = styled.div`display: flex; gap: 8px; align-items: center; > input { flex: 1; }`
const RemBtn = styled.button`border: none; background: none; color: #b00020; cursor: pointer; font-size: 14px; flex: none;`
const SlaBlock = styled.div`display: flex; flex-direction: column; gap: 8px; padding: 10px; border: 1px solid rgba(12,18,41,0.08); border-radius: 10px;`
const MonitorRow = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: wrap;`
const MonitorHelp = styled.p`margin: 6px 0 0; font-size: 12px; line-height: 1.5; color: #6b7280;`
const Footer = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #eee;`
const Ghost = styled.button`font: inherit; border: 1px solid rgba(12,18,41,0.16); background: #fff; border-radius: 8px; padding: 9px 16px; font-weight: 700; cursor: pointer;`
const Primary = styled.button`font: inherit; border: none; background: #0f172a; color: #fff; border-radius: 8px; padding: 9px 18px; font-weight: 700; cursor: pointer; &:disabled { opacity: 0.5; }`
const ContractorRow = styled.div`display: flex; gap: 10px; align-items: flex-end;`
const GatedFieldset = styled.fieldset`border: none; margin: 0; padding: 0; min-width: 0; display: flex; flex-direction: column; gap: 10px; &:disabled { opacity: 0.5; }`
const NewContractorPanel = styled.div`display: flex; flex-direction: column; gap: 10px; padding: 12px; border: 1px solid rgba(12,18,41,0.12); border-radius: 10px; background: #fafbfc;`
const NewContractorActions = styled.div`display: flex; justify-content: flex-end;`
const GateHint = styled.div`font-size: 12.5px; color: #d97706; background: #fef3c7; border: 1px solid #fde68a; border-radius: 8px; padding: 8px 10px;`
